import { InputWidgetButtonAction } from 'enums/InputWidgetButtonAction';
import { InputWidgetData } from 'interfaces/InputWidgetData';
import { InputWidgetElementData } from 'interfaces/InputWidgetElementData';
import { SurveyRequestCommandData } from 'interfaces/MessageData';

export const enum InputWidgetActionType {
    Receive = 'INPUT_WIDGET/RECEIVE',
    ReceiveSurvey = 'INPUT_WIDGET/RECEIVE_SURVEY',
    Close = 'INPUT_WIDGET/CLOSE',
    SendReply = 'INPUT_WIDGET/SEND_REPLY',
    SendSurveyReply = 'INPUT_WIDGET/SEND_SURVEY_REPLY',
    UpdateElement = 'INPUT_WIDGET/UPDATE_ELEMENT',
    SetElementErrors = 'INPUT_WIDGET/SET_ELEMENT_ERRORS',
    SetButtonEnabled = 'INPUT_WIDGET/SET_BUTTON_ENABLED',
    SetButtonHidden = 'INPUT_WIDGET/SET_BUTTON_HIDDEN',
    SendOtpCode = 'INPUT_WIDGET/SEND_OTP_CODE',
    ResetOtpTimestamp = 'INPUT_WIDGET/RESET_OTP_TIMESTAMP',
    SetOtpInProgress = 'INPUT_WIDGET/SET_OTP_IN_PROGRESS',
    ReplyButtonPressed = 'INPUT_WIDGET/REPLY_BUTTON_PRESSED',
    UpdateMasterDetails = 'INPUT_WIDGET/UPDATE_MASTER_DETAILS',
    RevealElementErrors = 'INPUT_WIDGET/REVEAL_ELEMENT_ERRORS',
    SetMasterElementValue = 'INPUT_WIDGET/SET_MASTER_ELEMENT_VALUE',
}

export type ReceiveInputWidgetAction = ReturnType<typeof receiveInputWidget>;
export const receiveInputWidget = (data: InputWidgetData) => ({
    type: InputWidgetActionType.Receive,
    payload: data,
});

export type ReceiveSurveyWidgetAction = ReturnType<typeof receiveSurveyWidget>;
export const receiveSurveyWidget = (data: SurveyRequestCommandData) => ({
    type: InputWidgetActionType.ReceiveSurvey,
    payload: data,
});

export type CloseInputWidgetAction = ReturnType<typeof closeInputWidget>;
export const closeInputWidget = () => ({
    type: InputWidgetActionType.Close,
});

export type SendInputWidgetReplyAction = ReturnType<typeof sendInputWidgetReply>;
export const sendInputWidgetReply = (action: InputWidgetButtonAction) => ({
    type: InputWidgetActionType.SendReply,
    payload: { action },
});

export type SendSurveyWidgetReplyAction = ReturnType<typeof sendSurveyWidgetReply>;
export const sendSurveyWidgetReply = (action: InputWidgetButtonAction) => ({
    type: InputWidgetActionType.SendSurveyReply,
    payload: { action },
});

export type UpdateInputWidgetElementAction = ReturnType<typeof updateInputWidgetElement>;
export const updateInputWidgetElement = <TElementData extends InputWidgetElementData>({
    elementId,
    elementData,
}: {
    elementId: string;
    elementData: TElementData;
}) => ({
    type: InputWidgetActionType.UpdateElement,
    payload: { elementId, elementData },
});

export type SetInputWidgetElementErrorsAction = ReturnType<typeof setInputWidgetElementErrors>;
export const setInputWidgetElementErrors = (
    elementId: string,
    errors: string[],
    revealNow = false,
) => ({
    type: InputWidgetActionType.SetElementErrors,
    payload: { elementId, errors, revealNow },
});

export type SetInputWidgetButtonEnabledAction = ReturnType<typeof setInputWidgetButtonEnabled>;
export const setInputWidgetButtonEnabled = (type: InputWidgetButtonAction, enabled: boolean) => ({
    type: InputWidgetActionType.SetButtonEnabled,
    payload: { type, enabled },
});

export type SetInputWidgetButtonHiddenAction = ReturnType<typeof setInputWidgetButtonHidden>;
export const setInputWidgetButtonHidden = (type: InputWidgetButtonAction, hidden: boolean) => ({
    type: InputWidgetActionType.SetButtonHidden,
    payload: { type, hidden },
});

export type ResetInputWidgetOtpTimestampAction = ReturnType<typeof resetInputWidgetOtpTimestamp>;
export const resetInputWidgetOtpTimestamp = () => ({
    type: InputWidgetActionType.ResetOtpTimestamp,
    payload: Date.now().valueOf(),
});

export type SetInputWidgetOtpInProgressAction = ReturnType<typeof setInputWidgetOtpInProgress>;
export const setInputWidgetOtpInProgress = (inProgress: boolean) => ({
    type: InputWidgetActionType.SetOtpInProgress,
    payload: inProgress,
});

export type InputWidgetSendOtpCodeAction = ReturnType<typeof inputWidgetSendOtpCode>;
export const inputWidgetSendOtpCode = (code: string) => ({
    type: InputWidgetActionType.SendOtpCode,
    payload: code,
});

export type InputWidgetReplyButtonPressedAction = ReturnType<typeof inputWidgetReplyButtonPressed>;
export const inputWidgetReplyButtonPressed = (type: InputWidgetButtonAction) => ({
    type: InputWidgetActionType.ReplyButtonPressed,
    payload: type,
});

export type UpdateInputWidgetMasterDetailsAction = ReturnType<
    typeof updateInputWidgetMasterDetails
>;
export const updateInputWidgetMasterDetails = (
    items: { id: string; value: string }[],
    clientSide = false,
) => ({
    type: InputWidgetActionType.UpdateMasterDetails,
    payload: { items, clientSide },
});

export type RevealInputWidgetElementErrorsAction = ReturnType<
    typeof revealInputWidgetElementErrors
>;
export const revealInputWidgetElementErrors = (elementId: string) => ({
    type: InputWidgetActionType.RevealElementErrors,
    payload: elementId,
});

export type SetInputWidgetMasterElementValueAction = ReturnType<
    typeof setInputWidgetMasterElementValue
>;
export const setInputWidgetMasterElementValue = (elementId: string, value: string) => ({
    type: InputWidgetActionType.SetMasterElementValue,
    payload: { elementId, value },
});
